import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast, notify } from "@/shared/toast";

interface AssignKidToBatchParams {
  batchId: number;
  kidId: number;
}

// Shared between BatchesPage.tsx and CoachKidDetail.tsx so both entry points
// use identical confirm-copy and query invalidation. Enrollment REPLACES any
// existing assignment (one-batch-per-player, by design — see the comment on
// POST /api/batches/:id/kids/:kidId in server/routes/batches.ts) — callers
// are responsible for confirming the move with the coach before calling
// mutate() when the kid already has a batch.
export function useAssignKidToBatch() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const safeToast = (t: any) => {
    try {
      toast(t);
    } catch {
      notify(t);
    }
  };

  return useMutation({
    mutationFn: async ({ batchId, kidId }: AssignKidToBatchParams) =>
      apiRequest("POST", `/api/batches/${batchId}/kids/${kidId}`),
    onSuccess: () => {
      safeToast({
        title: "Player assigned",
        description: "The batch roster has been updated.",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/batches"] });
      queryClient.invalidateQueries({ queryKey: ["/api/players"] });
      queryClient.invalidateQueries({ queryKey: ["player-sessions"] });
    },
    onError: (e: Error) => {
      safeToast({
        title: "Failed to assign player",
        description: e.message || "Please try again.",
        variant: "destructive",
      });
    },
  });
}
